import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, AlertCircle } from "lucide-react";

import { getPublicMarketUpdate } from "../api/prospectingReportsApi";
import collingsLogo from "../../../assets/collingslogo.png";
import { sanitizeHtml } from "../../../utils/sanitizeHtml";
import MarketUpdateEmailFrame from "../components/MarketUpdateEmailFrame";

/**
 * The web version of a market update, for the "view in browser" link and for an
 * agent previewing what their client list will receive. Public, no login.
 */
const MarketUpdatePublicPage = () => {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    getPublicMarketUpdate(token)
      .then(({ data }) => setReport(data))
      .catch(() => setError("We couldn't find that market update."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400">
        <Loader2 className="w-7 h-7 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
        <div className="max-w-md text-center">
          <AlertCircle className="w-10 h-10 mx-auto text-gray-300 mb-3" />
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // A property update is the same page the vendor got by email, rendered by the
  // server, so it shows in a frame rather than through the CRM's styles.
  if (report?.kind === "property") {
    return (
      <div className="min-h-screen bg-[#f3f4f6]">
        <MarketUpdateEmailFrame html={report.html || ""} title={report.title || "Market update"} width="100%" minHeight={800} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-5 py-4">
          <img src={collingsLogo} alt="Collings Real Estate" className="h-7 w-auto object-contain" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-8">
        <div className="bg-white rounded-xl border border-gray-100 p-6">
          <div
            className="prose prose-sm max-w-none text-gray-800"
            // Agent-authored content from a CRM template, with merge tags already
            // resolved server-side. Still sanitised: it renders on the CRM origin,
            // so a script or onerror= in a template would run for every reader.
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(report?.body_html || "") }}
          />
        </div>
      </main>
    </div>
  );
};

export default MarketUpdatePublicPage;
